import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApi } from '../../engine/api/context';
import { useAction } from '../../engine/api/hooks';
import { Button } from '../../engine/design/primitives/Button';
import { Dialog } from '../../engine/design/primitives/Overlay';
import { Field, Input, Switch } from '../../engine/design/primitives/Input';
import { Combobox } from '../../engine/design/primitives/Combobox';
import { DatePicker } from '../../engine/design/primitives/DatePicker';
import { RelationPicker } from '../../engine/fields/RelationPicker';
import { DurationEditor } from '../../engine/fields/registry';
import { durationField, relField, todayIso } from './data';

// "Log time on task": pick an open task (search by ref/title), the technician, a duration
// ("1:30", "90m", "1.5h"), the work date and a note → POST /project-tasks/:id/time. The entry
// is a real time_entry (rates snapshotted, inferred timesheet bucket). `?log=1` opens it.

const technicianField = relField('technician_id', 'Technician', 'technicians');
const minutesField = durationField('minutes', 'Duration');

export function LogTimeOnTaskAction() {
  const api = useApi();
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(() => params.has('log'));
  const [taskId, setTaskId] = useState<string | null>(null);
  const [tech, setTech] = useState<string | null>(null);
  const [minutes, setMinutes] = useState<number | null>(null);
  const [on, setOn] = useState(todayIso());
  const [note, setNote] = useState('');
  const [billable, setBillable] = useState(true);
  const log = useAction('project-tasks', 'time', {
    toast: { success: 'Time logged' },
    invalidate: ['my-tasks', 'time-entries', 'timesheets'],
    onSuccess: () => {
      setMinutes(null);
      setNote('');
      close();
    },
  });
  const close = () => {
    setOpen(false);
    if (params.has('log')) {
      const next = new URLSearchParams(params);
      next.delete('log');
      setParams(next, { replace: true });
    }
  };
  const loadTasks = async (q: string) => {
    const page = await api.resource('project-tasks').list({ q: q || undefined, limit: 20, sort: '-created_at', filter: [{ field: 'status', op: 'nin', value: ['done', 'cancelled'] }] });
    return page.data.map((t) => ({ value: String(t.id), label: `${String(t.ref)} ${String(t.title)}`, sub: t.status ? String(t.status).replace(/_/g, ' ') : undefined }));
  };
  const valid = Boolean(taskId && tech && minutes);
  return (
    <>
      <Button variant="primary" icon="timer" onClick={() => setOpen(true)}>
        Log time
      </Button>
      <Dialog open={open} onClose={close} title="Log time on a task" locked={log.isPending}>
        <form
          className="hf-stack"
          onSubmit={(e) => {
            e.preventDefault();
            if (!valid) return;
            log.mutate({ id: taskId!, body: { technician_id: tech, minutes, occurred_on: on, note: note || undefined, billable } });
          }}
        >
          <Field label="Task" required>
            <Combobox<string> value={taskId} onChange={(v) => setTaskId(v)} loadOptions={loadTasks} placeholder="Search open tasks…" autoFocus />
          </Field>
          <Field label="Technician" required>
            <RelationPicker field={technicianField} value={tech} onChange={(v) => setTech(v)} />
          </Field>
          <div className="hf-row" style={{ alignItems: 'flex-start' }}>
            <Field label="Duration" required>
              <DurationEditor field={minutesField} value={minutes} onChange={(v) => setMinutes(v == null ? null : Number(v))} />
            </Field>
            <Field label="Work date">
              <DatePicker value={on} onChange={(iso) => setOn(iso ?? todayIso())} />
            </Field>
          </div>
          <Field label="Note">
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What was done" />
          </Field>
          <Switch checked={billable} onChange={(e) => setBillable(e.target.checked)} label="Billable" />
          <div className="hf-form-actions">
            <Button variant="ghost" onClick={close} disabled={log.isPending}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" busy={log.isPending} disabled={!valid}>
              Log time
            </Button>
          </div>
        </form>
      </Dialog>
    </>
  );
}
